import {
  WEIGHT_UNITS,
  WEIGHT_UNIT_MAP,
  KG_PER_LB,
  LB_PER_KG,
  type WeightUnitKey,
  type WeightUnit,
} from "@/data/weightUnits";

/** Convert `value` from `from` weight unit to `to` weight unit. */
export function convertWeight(value: number, from: WeightUnitKey, to: WeightUnitKey): number {
  if (!Number.isFinite(value)) return NaN;
  const kg = value * WEIGHT_UNIT_MAP[from].kg;
  return kg / WEIGHT_UNIT_MAP[to].kg;
}

/** Convert a value to all supported units. */
export function convertWeightToAll(
  value: number,
  from: WeightUnitKey,
): { unit: WeightUnit; value: number }[] {
  const kg = value * WEIGHT_UNIT_MAP[from].kg;
  return WEIGHT_UNITS.map((u) => ({ unit: u, value: kg / u.kg }));
}

export function kgToPounds(kg: number): number {
  return kg * LB_PER_KG;
}

export function poundsToKg(lb: number): number {
  return lb * KG_PER_LB;
}

/**
 * Rough BMI category from a weight + assumed average BD adult height (1.65 m),
 * for a "body weight" hint when users enter realistic weights. Pass a custom
 * height (m) for more accurate categorisation.
 */
export type BmiCategory = "underweight" | "normal" | "overweight" | "obese";

export function bmiCategory(kg: number, heightM = 1.65): { bmi: number; category: BmiCategory } | null {
  if (!Number.isFinite(kg) || kg <= 0 || heightM <= 0) return null;
  const bmi = kg / (heightM * heightM);
  let category: BmiCategory = "normal";
  if (bmi < 18.5) category = "underweight";
  else if (bmi < 25) category = "normal";
  else if (bmi < 30) category = "overweight";
  else category = "obese";
  return { bmi, category };
}

export { WEIGHT_UNITS, WEIGHT_UNIT_MAP, KG_PER_LB, LB_PER_KG };
export type { WeightUnitKey, WeightUnit };
